import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AdminPage.css';
import AdminKiosks from './AdminKiosks';
import AdminMeetingRooms from './AdminMeetingRooms';
import AdminDatabase from './AdminDatabase';
import AdminMessages from './AdminMessages';

function AdminPage({ user, onLogout }) {
    const [activeTab, setActiveTab] = useState('users');
    const [userPage, setUserPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [users, setUsers] = useState([]);
    const [desks, setDesks] = useState([]);
    const [devices, setDevices] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [units, setUnits] = useState([]);
    const [deskUnitMapping, setDeskUnitMapping] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [statusChangeModal, setStatusChangeModal] = useState(false);
    const [statusChangeData, setStatusChangeData] = useState({});
    const [impactedBookings, setImpactedBookings] = useState([]);
    const [availableUnits, setAvailableUnits] = useState([]);
    const [availableZones, setAvailableZones] = useState([]);
    // Unit admin modal state
    const [adminModalOpen, setAdminModalOpen] = useState(false);
    const [adminModalUnit, setAdminModalUnit] = useState('');
    const [adminModalSelected, setAdminModalSelected] = useState([]);
    const [zoneDesks, setZoneDesks] = useState([]);
    const [selectedDeskIds, setSelectedDeskIds] = useState([]);
    // Filter state variables
    const [userFilters, setUserFilters] = useState({ search: '', unit: '', admin: '' });
    const [deskFilters, setDeskFilters] = useState({ search: '', zone: '', unit: '', status: '' });
    const [unitFilters, setUnitFilters] = useState({ search: '' });
    // Flex pool state
    const [flexPoolDesks, setFlexPoolDesks] = useState([]);
    const [flexPoolFilters, setFlexPoolFilters] = useState({ search: '', zone: '' });
    // Device reassignment modal state
    const [deviceReassignModal, setDeviceReassignModal] = useState(false);
    const [selectedDeviceForReassign, setSelectedDeviceForReassign] = useState(null);
    const [selectedNewDeskForDevice, setSelectedNewDeskForDevice] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (activeTab === 'users') fetchUsers();
        else if (activeTab === 'desks') {
            fetchDesks();
            fetchDevices();
        }
        else if (activeTab === 'holidays') fetchHolidays();
        else if (activeTab === 'units') {
            fetchUnits();
            fetchDeskUnitMapping();
        }
        else if (activeTab === 'flexpool') {
            fetchFlexPoolDesks();
        }
    }, [activeTab]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        const isModalOpen = showModal || statusChangeModal;
        if (isModalOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [showModal, statusChangeModal]);

    // Auto-clear success/error messages after 3 seconds
    useEffect(() => {
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess('');
                setError('');
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/users');
            setUsers(response.data);
        } catch (err) {
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const fetchDesks = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/desks');
            setDesks(response.data);
        } catch (err) {
            setError('Failed to load desks');
        } finally {
            setLoading(false);
        }
    };

    const fetchHolidays = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/holidays');
            setHolidays(response.data);
        } catch (err) {
            setError('Failed to load holidays');
        } finally {
            setLoading(false);
        }
    };

    const fetchUnits = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/units');
            setUnits(response.data);
        } catch (err) {
            setError('Failed to load units');
        } finally {
            setLoading(false);
        }
    };

    const fetchDeskUnitMapping = async () => {
        try {
            const response = await axios.get('/api/admin/unit-desk-mapping');
            setDeskUnitMapping(response.data);
        } catch (err) {
            console.error('Failed to load desk-unit mapping', err);
        }
    };

    const fetchDevices = async () => {
        try {
            const response = await axios.get('/api/admin/devices');
            setDevices(response.data);
        } catch (err) {
            console.error('Failed to load devices', err);
        }
    };

    const handleDeviceReassign = async () => {
        if (!selectedDeviceForReassign || !selectedNewDeskForDevice) {
            setError('Please select both device and new desk');
            return;
        }

        try {
            const response = await axios.put(
                `/api/admin/devices/${selectedDeviceForReassign}/reassign`,
                { desk_id: selectedNewDeskForDevice }
            );
            setSuccess(response.data.message || 'Device reassigned successfully');
            setDeviceReassignModal(false);
            setSelectedDeviceForReassign(null);
            setSelectedNewDeskForDevice('');
            // Refresh both desks and devices
            await fetchDesks();
            await fetchDevices();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to reassign device');
        }
    };

    // Filter functions
    const getFilteredUsers = () => {
        return users.filter(u => {
            const searchMatch = !userFilters.search ||
                u.email.toLowerCase().includes(userFilters.search.toLowerCase()) ||
                (u.name && u.name.toLowerCase().includes(userFilters.search.toLowerCase()));
            
            // Check both primary unit and additional units
            let unitMatch = !userFilters.unit;
            if (userFilters.unit && !unitMatch) {
                // Check if user's primary unit matches
                unitMatch = u.unit === userFilters.unit;
                
                // Check if user's additional units include the selected unit
                if (!unitMatch && u.additional_units) {
                    const additionalUnitsList = u.additional_units.split(',').map(unit => unit.trim());
                    unitMatch = additionalUnitsList.includes(userFilters.unit);
                }
            }
            
            const adminMatch = !userFilters.admin || (userFilters.admin === 'yes' ? u.is_admin : !u.is_admin);
            return searchMatch && unitMatch && adminMatch;
        });
    };

    const getFilteredDesks = () => {
        return desks.filter(d => {
            const searchMatch = !deskFilters.search || d.id.toLowerCase().includes(deskFilters.search.toLowerCase());
            const zoneMatch = !deskFilters.zone || d.zone === deskFilters.zone;
            const unitMatch = !deskFilters.unit || d.unit === deskFilters.unit;
            const statusMatch = !deskFilters.status || d.status === deskFilters.status;
            return searchMatch && zoneMatch && unitMatch && statusMatch;
        });
    };

    const getFilteredUnits = () => {
        return units.filter(u => {
            const searchMatch = !unitFilters.search || u.name.toLowerCase().includes(unitFilters.search.toLowerCase());
            return searchMatch;
        });
    };

    const formatUserPhone = (user) => {
        if (!user) return '-';
        const country = user.phone_country_code || '';
        const number = user.phone_number || '';
        const combined = `${country} ${number}`.trim();
        return combined || '-';
    };

    const clearUserFilters = () => setUserFilters({ search: '', unit: '', admin: '' });
    const clearDeskFilters = () => setDeskFilters({ search: '', zone: '', unit: '', status: '' });
    const clearUnitFilters = () => setUnitFilters({ search: '' });

    // Flex Pool Functions
    const fetchFlexPoolDesks = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/flex-pool-desks');
            setFlexPoolDesks(response.data);
        } catch (err) {
            setError('Failed to load flex pool desks');
        } finally {
            setLoading(false);
        }
    };

    const addDeskToFlexPool = async (deskId, zone) => {
        try {
            await axios.post('/api/admin/flex-pool-desks', { desk_id: deskId, zone });
            setSuccess('Desk added to flex pool');
            fetchFlexPoolDesks();
            setFormData({});
            setShowModal(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add desk to flex pool');
        }
    };

    const removeDeskFromFlexPool = async (deskId) => {
        if (!window.confirm('Remove this desk from flex pool?')) return;
        try {
            await axios.delete(`/api/admin/flex-pool-desks/${deskId}`);
            setSuccess('Desk removed from flex pool');
            fetchFlexPoolDesks();
        } catch (err) {
            setError('Failed to remove desk from flex pool');
        }
    };

    const updateFlexPoolDeskStatus = async (deskId, newStatus) => {
        try {
            await axios.put(`/api/admin/flex-pool-desks/${deskId}`, { status: newStatus });
            setSuccess('Flex pool desk status updated');
            fetchFlexPoolDesks();
        } catch (err) {
            setError('Failed to update desk status');
        }
    };

    const getFilteredFlexPoolDesks = () => {
        return flexPoolDesks.filter(d => {
            const searchMatch = !flexPoolFilters.search || d.desk_id.toLowerCase().includes(flexPoolFilters.search.toLowerCase());
            const zoneMatch = !flexPoolFilters.zone || d.zone === flexPoolFilters.zone;
            return searchMatch && zoneMatch;
        });
    };

    useEffect(() => {
        // Fetch available units and zones for dropdowns
        const fetchDropdownData = async () => {
            try {
                const unitsRes = await axios.get('/api/units');
                setAvailableUnits(unitsRes.data);

                const desksRes = await axios.get('/api/admin/desks');
                const uniqueZones = [...new Set(desksRes.data.map(d => d.zone))].sort();
                setAvailableZones(uniqueZones);
            } catch (err) {
                console.error('Failed to load dropdown data', err);
            }
        };
        fetchDropdownData();
    }, []);

    const handleAddUser = () => {
        setEditingId(null);
        setFormData({ email: '', name: '', unit: '', is_admin: false });
        setShowModal(true);
    };

    const handleEditUser = (userToEdit) => {
        setEditingId(userToEdit.id);
        setFormData({
            id: userToEdit.id,
            email: userToEdit.email,
            name: userToEdit.name,
            unit: userToEdit.unit,
            is_admin: userToEdit.is_admin,
            alternate_email: userToEdit.alternate_email,
            phone_country_code: userToEdit.phone_country_code,
            phone_number: userToEdit.phone_number,
            department: userToEdit.department,
            zone: userToEdit.zone,
            monitor_preference: userToEdit.monitor_preference,
            notification_preference: userToEdit.notification_preference,
            show_avatar: userToEdit.show_avatar,
            unit_admin: userToEdit.unit_admin,
            profile_complete: userToEdit.profile_complete,
            profile_picture: userToEdit.profile_picture,
            locker_pin: userToEdit.locker_pin,
            adjustable_monitor_priority: userToEdit.adjustable_monitor_priority
        });
        setShowModal(true);
    };

    const handleAddDesk = () => {
        setEditingId(null);
        setFormData({ id: '', zone: '', unit: '', monitor_setup: 'Single Monitor', equipment: '', status: 'available' });
        setShowModal(true);
    };

    const handleEditDesk = (deskToEdit) => {
        setEditingId(deskToEdit.id);
        setFormData({
            id: deskToEdit.id,
            zone: deskToEdit.zone,
            unit: deskToEdit.unit,
            monitor_setup: deskToEdit.monitor_setup || 'Single Monitor',
            equipment: deskToEdit.equipment || '',
            status: deskToEdit.status || 'available',
            reserved_for: deskToEdit.reserved_for || '',
            has_display: deskToEdit.has_display || 0,
            adjustable_monitor: deskToEdit.adjustable_monitor || 0,
            originalStatus: deskToEdit.status || 'available'
        });
        setShowModal(true);
    };

    const handleAddHoliday = () => {
        setFormData({ date: '', name: '' });
        setShowModal(true);
    };

    const handleSaveUser = async () => {
        try {
            setError('');
            setSuccess('');

            // If unit is new, create it first
            if (formData.unit && !availableUnits.includes(formData.unit)) {
                try {
                    await axios.post('/api/admin/units', {
                        name: formData.unit,
                        allow_reserve: true
                    });
                } catch (err) {
                    // Ignore if unit already exists
                    if (!err.response?.data?.error?.includes('already exists')) {
                        throw err;
                    }
                }
            }

            if (editingId) {
                await axios.put(`/api/admin/users/${editingId}`, formData);
                setSuccess('User updated successfully');
            } else {
                await axios.post('/api/admin/users', formData);
                setSuccess('User added successfully');
            }
            setShowModal(false);
            setFormData({});
            setEditingId(null);
            fetchUsers();

            // Refresh available units
            const unitsRes = await axios.get('/api/units');
            setAvailableUnits(unitsRes.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save user');
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure?')) {
            try {
                setError('');
                setSuccess('');
                await axios.delete(`/api/admin/users/${userId}`);
                setSuccess('User deleted');
                fetchUsers();
            } catch (err) {
                setError('Failed to delete user');
            }
        }
    };

    const handleResetPassword = async (userEmail, userName) => {
        if (window.confirm(`Reset password for ${userName || userEmail}?\n\nThis will send a password reset link to their email.`)) {
            try {
                setError('');
                setSuccess('');
                await axios.post('/api/admin/reset-password', { email: userEmail });
                setSuccess(`Password reset link sent to ${userEmail}`);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to reset password');
            }
        }
    };

    const handleSaveDesk = async () => {
        try {
            setError('');
            setSuccess('');

            // Validate desk ID is not empty
            if (!formData.id || !formData.id.trim()) {
                setError('Desk ID is required');
                return;
            }

            // Validate zone is not empty
            if (!formData.zone || !formData.zone.trim()) {
                setError('Zone is required');
                return;
            }

            // Check if desk ID already exists (when adding new desk)
            if (!editingId) {
                const existingDesk = desks.find(d => d.id === formData.id);
                if (existingDesk) {
                    setError(`Desk ID "${formData.id}" already exists. Please use a different ID or edit the existing desk.`);
                    return;
                }
            }

            // If unit is new, create it first
            if (formData.unit && !availableUnits.includes(formData.unit)) {
                try {
                    await axios.post('/api/admin/units', {
                        name: formData.unit,
                        allow_reserve: true
                    });
                } catch (err) {
                    // Ignore if unit already exists
                    if (!err.response?.data?.error?.includes('already exists')) {
                        throw err;
                    }
                }
            }

            // Check if status is changing to maintenance or reserved
            if (editingId && formData.originalStatus !== formData.status &&
                (formData.status === 'maintenance' || formData.status === 'reserved')) {
                // Check for impacted bookings
                await checkBookingImpact();
                return;
            }

            // Automatically set adjustable_monitor flag based on monitor_setup
            const dataToSave = {
                ...formData,
                adjustable_monitor: formData.monitor_setup === 'Adjustable Monitor' ? 1 : 0
            };

            if (editingId) {
                await axios.put(`/api/admin/desks/${editingId}`, dataToSave);
                setSuccess('Desk updated successfully');
            } else {
                await axios.post('/api/admin/desks', dataToSave);
                setSuccess('Desk added successfully');
            }
            setShowModal(false);
            setFormData({});
            setEditingId(null);
            fetchDesks();

            // Refresh available units and zones
            const [unitsRes, desksRes] = await Promise.all([
                axios.get('/api/units'),
                axios.get('/api/admin/desks')
            ]);
            setAvailableUnits(unitsRes.data.map(u => u.name));
            const zones = [...new Set(desksRes.data.map(d => d.zone))].filter(Boolean).sort();
            setAvailableZones(zones);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save desk');
        }
    };

    const handleDeleteDesk = async (deskId) => {
        if (window.confirm('Are you sure you want to delete this desk?')) {
            try {
                setError('');
                setSuccess('');
                await axios.delete(`/api/admin/desks/${deskId}`);
                setSuccess('Desk deleted');
                fetchDesks();
            } catch (err) {
                setError('Failed to delete desk');
            }
        }
    };

    const handleSaveHoliday = async () => {
        try {
            setError('');
            setSuccess('');
            await axios.post('/api/admin/holidays', formData);
            setSuccess('Holiday added');
            setShowModal(false);
            setFormData({});
            fetchHolidays();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add holiday');
        }
    };

    const handleDeleteHoliday = async (date) => {
        if (window.confirm('Are you sure?')) {
            try {
                setError('');
                setSuccess('');
                await axios.delete(`/api/admin/holidays/${date}`);
                setSuccess('Holiday deleted');
                fetchHolidays();
            } catch (err) {
                setError('Failed to delete holiday');
            }
        }
    };

    const handleAddUnit = () => {
        setEditingId(null);
        setFormData({ name: '', allow_reserve: true, zone: '', desk_ids: [] });
        setShowModal(true);
    };

    const handleEditUnit = (unitToEdit) => {
        setEditingId(unitToEdit.name);
        setFormData({
            name: unitToEdit.name,
            allow_reserve: unitToEdit.allow_reserve === 1,
            unit_admins: unitToEdit.unit_admins || '',
        });
        setShowModal(true);
    };

    const handleSaveUnit = async () => {
        try {
            setError('');
            setSuccess('');
            if (editingId) {
                await axios.put(`/api/admin/units/${editingId}`, formData);
                setSuccess('Unit updated successfully');
            } else {
                await axios.post('/api/admin/units', formData);
                setSuccess('Unit added successfully');
            }
            setShowModal(false);
            setFormData({});
            setEditingId(null);
            fetchUnits();
            // Refresh available units
            const unitsRes = await axios.get('/api/units');
            setAvailableUnits(unitsRes.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save unit');
        }
    };

    const handleDeleteUnit = async (unitName) => {
        if (window.confirm(`Are you sure you want to delete unit "${unitName}"?\n\nThis will only work if the unit is not assigned to any users or desks.`)) {
            try {
                setError('');
                setSuccess('');
                await axios.delete(`/api/admin/units/${unitName}`);
                setSuccess('Unit deleted successfully');
                fetchUnits();
                // Refresh available units
                const unitsRes = await axios.get('/api/units');
                setAvailableUnits(unitsRes.data);
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to delete unit');
            }
        }
    };

    const fetchDesksByZone = async (zone) => {
        if (!zone) {
            setZoneDesks([]);
            setSelectedDeskIds([]);
            return;
        }
        try {
            const response = await axios.get('/api/admin/desks');
            const desksInZone = response.data.filter(d => d.zone === zone);
            setZoneDesks(desksInZone);
            setSelectedDeskIds([]);
        } catch (err) {
            console.error('Failed to fetch desks by zone', err);
            setZoneDesks([]);
        }
    };

    const handleAssignDesksToUnit = async () => {
        try {
            setError('');
            setSuccess('');
            const { name, zone } = formData;

            if (!name) {
                setError('Please select a unit');
                return;
            }

            // Check if we're assigning specific desks or whole zone
            const deskIdsToAssign = selectedDeskIds.length > 0 ? selectedDeskIds : null;

            if (!zone && !deskIdsToAssign) {
                setError('Please select a zone or specific desks');
                return;
            }

            const response = await axios.post('/api/admin/assign-desks-to-unit', {
                unit: name,
                zone: deskIdsToAssign ? null : (zone || null),
                desk_ids: deskIdsToAssign
            });

            setSuccess(response.data.message);
            setShowModal(false);
            setFormData({});
            setZoneDesks([]);
            setSelectedDeskIds([]);
            fetchDeskUnitMapping();
            fetchDesks();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to assign desks');
        }
    };

    const checkBookingImpact = async () => {
        try {
            const response = await axios.get(`/api/admin/desk-booking-impact/${editingId}?startDate=${statusChangeData.startDate || new Date().toISOString().split('T')[0]}&endDate=${statusChangeData.endDate || '2099-12-31'}`);
            setImpactedBookings(response.data.impactedBookings || []);
            setStatusChangeData({
                ...formData,
                startDate: statusChangeData.startDate || new Date().toISOString().split('T')[0],
                endDate: statusChangeData.endDate || '2099-12-31',
                reservedUsers: statusChangeData.reservedUsers || ''
            });
            setStatusChangeModal(true);
            setShowModal(false);
        } catch (err) {
            setError('Failed to check booking impact');
        }
    };

    const confirmStatusChange = async () => {
        try {
            setError('');
            setSuccess('');

            const updateData = {
                ...formData,
                startDate: statusChangeData.startDate,
                endDate: statusChangeData.endDate,
                reservedUsers: statusChangeData.reservedUsers,
                reserved_for: statusChangeData.reserved_for || formData.reserved_for || null,
                cancelBookings: impactedBookings.length > 0
            };

            await axios.put(`/api/admin/desks/${editingId}/status-change`, updateData);
            setSuccess(`Desk status updated. ${impactedBookings.length} booking(s) cancelled and notifications sent.`);

            setStatusChangeModal(false);
            setFormData({});
            setEditingId(null);
            setImpactedBookings([]);
            fetchDesks();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update desk status');
        }
    };

    // Reset user page when filters change
    useEffect(() => {
        setUserPage(1);
    }, [userFilters]);

    // Pagination logic for users
    const filteredUsers = getFilteredUsers();
    const totalUserPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const currentUsers = filteredUsers.slice((userPage - 1) * itemsPerPage, userPage * itemsPerPage);

    return (
        <div className="admin-page">
            <div className="header">
                <div className="header-content">
                    <h1>⚙️ Admin Panel</h1>
                    <div className="user-info">
                        <div className="user-name">{user.name || user.email}</div>
                        <button onClick={() => navigate('/dashboard')} className="btn-back-dashboard">
                            Back to Dashboard
                        </button>
                        <button onClick={onLogout} className="btn-logout">
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            <div className="container">
                {error && <div className="error">{error}</div>}
                {success && <div className="success">{success}</div>}

                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        👥 Users ({users.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'desks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('desks')}
                    >
                        🪑 Desks ({desks.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'units' ? 'active' : ''}`}
                        onClick={() => setActiveTab('units')}
                    >
                        🏢 Units ({units.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'holidays' ? 'active' : ''}`}
                        onClick={() => setActiveTab('holidays')}
                    >
                        📅 Holidays ({holidays.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'kiosks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('kiosks')}
                    >
                        🖥️ Kiosk Management
                    </button>
                    <button
                        className={`tab ${activeTab === 'meetingrooms' ? 'active' : ''}`}
                        onClick={() => setActiveTab('meetingrooms')}
                    >
                        🏢 Meeting Rooms
                    </button>
                    <button
                        className={`tab ${activeTab === 'flexpool' ? 'active' : ''}`}
                        onClick={() => setActiveTab('flexpool')}
                    >
                        🔄 Flex Pool ({flexPoolDesks.length})
                    </button>
                    <button
                        className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('messages')}
                    >
                        📬 Messages
                    </button>
                    <button
                        className={`tab ${activeTab === 'database' ? 'active' : ''}`}
                        onClick={() => setActiveTab('database')}
                    >
                        🗄️ Database
                    </button>
                </div>

                <div className="card">
                    {activeTab === 'users' && (
                        <div>
                            <div className="section-header">
                                <h2>Manage Users</h2>
                                <button onClick={handleAddUser} className="btn-add">
                                    + Add User
                                </button>
                            </div>
                            {/* Users Filter Menu */}
                            <div className="filter-menu" style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '5px',
                                marginBottom: '15px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: '12px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>🔍 Search</label>
                                        <input
                                            type="text"
                                            placeholder="Email or name..."
                                            value={userFilters.search}
                                            onChange={(e) => setUserFilters({ ...userFilters, search: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Unit</label>
                                        <select
                                            value={userFilters.unit}
                                            onChange={(e) => setUserFilters({ ...userFilters, unit: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">All Units</option>
                                            {[...new Set(users.map(u => u.unit).filter(Boolean))].sort().map(unit => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Admin</label>
                                        <select
                                            value={userFilters.admin}
                                            onChange={(e) => setUserFilters({ ...userFilters, admin: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">All Users</option>
                                            <option value="yes">Admin Only</option>
                                            <option value="no">Non-Admin Only</option>
                                        </select>
                                    </div>
                                    <button
                                        onClick={clearUserFilters}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#f0f0f0',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ✕ Clear Filters
                                    </button>
                                </div>
                                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                                    Showing {filteredUsers.length > 0 ? (userPage - 1) * itemsPerPage + 1 : 0}-{Math.min(userPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
                                </p>
                            </div>
                            {loading ? (
                                <p>Loading...</p>
                            ) : (
                                <div className="table-wrapper" style={{ minHeight: '350px' }}>
                                    <table className="table users-table">
                                        <thead>
                                            <tr>
                                                <th>Email</th>
                                                <th>Name</th>
                                                <th>Unit</th>
                                                <th>Phone Number</th>
                                                <th>Locker No.</th>
                                                <th>Locker PIN</th>
                                                <th>Admin</th>
                                                <th>Priority</th>
                                                <th>Profile Complete</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentUsers.map(u => (
                                                <tr key={u.id}>
                                                    <td>{u.email}</td>
                                                    <td>{u.name || '-'}</td>
                                                    <td>{u.unit || '-'}</td>
                                                    <td>{formatUserPhone(u)}</td>
                                                    <td style={{ fontWeight: 'bold', fontSize: '14px', color: '#555' }}>
                                                        {u.locker_number || '-'}
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontWeight: 'bold', fontSize: '14px', color: '#0072CE' }}>
                                                        {u.locker_pin || '----'}
                                                    </td>
                                                    <td>{u.is_admin ? '✓' : '-'}</td>
                                                    <td style={{ fontSize: '18px', textAlign: 'center' }}>
                                                        {u.adjustable_monitor_priority ? '♿' : '-'}
                                                    </td>
                                                    <td>{u.profile_complete ? '✓' : '-'}</td>
                                                    <td>
                                                        <div className="action-dropdown">
                                                            <button className="btn-actions">⋮</button>
                                                            <div className="dropdown-menu">
                                                                <button
                                                                    onClick={() => handleEditUser(u)}
                                                                    className="dropdown-item edit"
                                                                >
                                                                    ✏️ Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleResetPassword(u.email, u.name)}
                                                                    className="dropdown-item reset"
                                                                >
                                                                    🔑 Reset Password
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteUser(u.id)}
                                                                    className="dropdown-item delete"
                                                                >
                                                                    🗑️ Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* User Pagination Controls */}
                            {filteredUsers.length > itemsPerPage && (
                                <div className="pagination-controls" style={{
                                    marginTop: '20px',
                                    display: 'flex',
                                    gap: '20px',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <button
                                        onClick={() => setUserPage(prev => Math.max(prev - 1, 1))}
                                        disabled={userPage === 1}
                                        style={{
                                            padding: '8px 16px',
                                            border: '1px solid #0b78d1',
                                            background: userPage === 1 ? '#f0f0f0' : 'white',
                                            color: userPage === 1 ? '#999' : '#0b78d1',
                                            borderRadius: '4px',
                                            cursor: userPage === 1 ? 'not-allowed' : 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        ← Previous
                                    </button>
                                    <span style={{ fontWeight: 'bold', color: '#666' }}>
                                        Page {userPage} of {totalUserPages}
                                    </span>
                                    <button
                                        onClick={() => setUserPage(prev => Math.min(prev + 1, totalUserPages))}
                                        disabled={userPage === totalUserPages}
                                        style={{
                                            padding: '8px 16px',
                                            border: '1px solid #0b78d1',
                                            background: userPage === totalUserPages ? '#f0f0f0' : 'white',
                                            color: userPage === totalUserPages ? '#999' : '#0b78d1',
                                            borderRadius: '4px',
                                            cursor: userPage === totalUserPages ? 'not-allowed' : 'pointer',
                                            fontWeight: '600'
                                        }}
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'desks' && (
                        <div>
                            <div className="section-header">
                                <h2>Manage Desks</h2>
                                <button onClick={handleAddDesk} className="btn-add">
                                    + Add Desk
                                </button>
                            </div>
                            {/* Desks Filter Menu */}
                            <div className="filter-menu" style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '5px',
                                marginBottom: '15px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '12px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>🔍 Desk ID</label>
                                        <input
                                            type="text"
                                            placeholder="Search desk ID..."
                                            value={deskFilters.search}
                                            onChange={(e) => setDeskFilters({ ...deskFilters, search: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Zone</label>
                                        <select
                                            value={deskFilters.zone}
                                            onChange={(e) => setDeskFilters({ ...deskFilters, zone: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">All Zones</option>
                                            {availableZones.sort().map(zone => (
                                                <option key={zone} value={zone}>{zone}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Unit</label>
                                        <select
                                            value={deskFilters.unit}
                                            onChange={(e) => setDeskFilters({ ...deskFilters, unit: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">All Units</option>
                                            {[...new Set(desks.map(d => d.unit).filter(Boolean))].sort().map(unit => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Status</label>
                                        <select
                                            value={deskFilters.status}
                                            onChange={(e) => setDeskFilters({ ...deskFilters, status: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">All Status</option>
                                            {[...new Set(desks.map(d => d.status || 'Available'))].sort().map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        onClick={clearDeskFilters}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#f0f0f0',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ✕ Clear Filters
                                    </button>
                                </div>
                                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                                    Showing {getFilteredDesks().length} of {desks.length} desks
                                </p>
                            </div>
                            {loading ? (
                                <p>Loading...</p>
                            ) : (
                                <div className="table-wrapper">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Desk ID</th>
                                                <th>Zone</th>
                                                <th>Unit</th>
                                                <th>Monitor</th>
                                                <th>Status</th>
                                                <th>Reserved For</th>
                                                <th>Device ID</th>
                                                <th>Device Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getFilteredDesks().map(d => {
                                                const assignedDevice = devices.find(dev => dev.desk_id === d.id);
                                                return (
                                                    <tr key={d.id}>
                                                        <td><strong>{d.id}</strong></td>
                                                        <td>{d.zone}</td>
                                                        <td>{d.unit}</td>
                                                        <td>{d.monitor_setup || '-'}</td>
                                                        <td>{d.status || 'Available'}</td>
                                                        <td>
                                                            {d.status === 'reserved' && d.reserved_for ? (
                                                                <span style={{ color: '#0072CE', fontWeight: 'bold' }}>
                                                                    {users.find(u => u.email === d.reserved_for)?.name || d.reserved_for}
                                                                </span>
                                                            ) : '-'}
                                                        </td>
                                                        <td>
                                                            {assignedDevice ? (
                                                                <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                                                    {assignedDevice.device_id}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#999' }}>-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {assignedDevice ? (
                                                                <span style={{
                                                                    padding: '4px 8px',
                                                                    borderRadius: '3px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 'bold',
                                                                    backgroundColor: assignedDevice.status === 'connected' ? '#d4edda' : '#f8d7da',
                                                                    color: assignedDevice.status === 'connected' ? '#155724' : '#721c24'
                                                                }}>
                                                                    {assignedDevice.status === 'connected' ? '🟢 Connected' : '🔴 Offline'}
                                                                </span>
                                                            ) : (
                                                                <span style={{ color: '#999' }}>-</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <div className="action-dropdown">
                                                                <button className="btn-actions">⋮</button>
                                                                <div className="dropdown-menu">
                                                                    <button
                                                                        onClick={() => handleEditDesk(d)}
                                                                        className="dropdown-item edit"
                                                                    >
                                                                        ✏️ Edit
                                                                    </button>
                                                                    {assignedDevice && (
                                                                        <button
                                                                            onClick={() => {
                                                                                setSelectedDeviceForReassign(assignedDevice.device_id);
                                                                                setSelectedNewDeskForDevice('');
                                                                                setDeviceReassignModal(true);
                                                                            }}
                                                                            className="dropdown-item"
                                                                            style={{ color: '#ff9800' }}
                                                                        >
                                                                            🔄 Reassign Device
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => handleDeleteDesk(d.id)}
                                                                        className="dropdown-item delete"
                                                                    >
                                                                        🗑️ Delete
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'holidays' && (
                        <div>
                            <div className="section-header">
                                <h2>Manage Holidays</h2>
                                <button onClick={handleAddHoliday} className="btn-add">
                                    + Add Holiday
                                </button>
                            </div>
                            {loading ? (
                                <p>Loading...</p>
                            ) : (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Name</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {holidays.map(h => (
                                            <tr key={h.id}>
                                                <td>{h.date}</td>
                                                <td>{h.name}</td>
                                                <td>
                                                    <button
                                                        onClick={() => handleDeleteHoliday(h.date)}
                                                        className="btn-delete"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                    {activeTab === 'units' && (
                        <div>
                            <div className="section-header">
                                <h2>Manage Units</h2>
                                <div>
                                    <button
                                        onClick={() => {
                                            setFormData({ action: 'assign', name: '', zone: '' });
                                            setShowModal(true);
                                        }}
                                        className="btn-add"
                                        style={{ marginRight: '10px' }}
                                    >
                                        🔗 Assign Desks to Unit
                                    </button>
                                    <button onClick={handleAddUnit} className="btn-add">
                                        + Add Unit
                                    </button>
                                </div>
                            </div>
                            {/* Units Filter Menu */}
                            <div className="filter-menu" style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '5px',
                                marginBottom: '15px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: '12px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>🔍 Search</label>
                                        <input
                                            type="text"
                                            placeholder="Unit name..."
                                            value={unitFilters.search}
                                            onChange={(e) => setUnitFilters({ ...unitFilters, search: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <button
                                        onClick={clearUnitFilters}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#f0f0f0',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ✕ Clear Filters
                                    </button>
                                </div>
                                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                                    Showing {getFilteredUnits().length} of {units.length} units
                                </p>
                            </div>
                            {loading ? (
                                <p>Loading...</p>
                            ) : (
                                <div className="table-wrapper">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Unit Name</th>
                                                <th>Allow Reserve</th>
                                                <th>Assigned Desks</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getFilteredUnits().map(unit => {
                                                const mapping = deskUnitMapping.filter(m => m.unit === unit.name);
                                                const totalDesks = mapping.reduce((sum, m) => sum + m.desk_count, 0);
                                                return (
                                                    <tr key={unit.name}>
                                                        <td style={{ padding: '12px 15px', verticalAlign: 'top' }}><strong>{unit.name}</strong></td>
                                                        <td style={{ padding: '12px 15px', verticalAlign: 'top' }}>{unit.allow_reserve ? '✓' : '-'}</td>
                                                        <td style={{ padding: '12px 15px', verticalAlign: 'top' }}>
                                                            {totalDesks > 0 ? (
                                                                <div>
                                                                    {totalDesks} desk(s)
                                                                    {mapping.length > 0 && (
                                                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                                            {mapping.map(m => `${m.zone}: ${m.desk_count}`).join(', ')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span style={{ color: '#999' }}>No desks assigned</span>
                                                            )}
                                                            {unit.unit_admins && typeof unit.unit_admins === 'string' && (
                                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', borderTop: '1px solid #eee', paddingTop: '4px' }}>
                                                                    <strong>Admins:</strong> <br />
                                                                    {unit.unit_admins.split(',').map((admin, idx) => (
                                                                        <span key={idx} style={{ display: 'block', marginBottom: '2px' }}>{admin.trim()}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '12px 15px', verticalAlign: 'top' }}>
                                                            <div className="action-dropdown">
                                                                <button className="btn-actions">⋮</button>
                                                                <div className="dropdown-menu">
                                                                    <button
                                                                        onClick={() => handleEditUnit(unit)}
                                                                        className="dropdown-item edit"
                                                                    >
                                                                        ✏️ Edit
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteUnit(unit.name)}
                                                                        className="dropdown-item delete"
                                                                    >
                                                                        🗑️ Delete
                                                                    </button>
                                                                    <button
                                                                        onClick={() => {
                                                                            // open assign/admin modal
                                                                            setAdminModalUnit(unit.name);
                                                                            const existing = (unit.unit_admins || '').split(',').map(s => s.trim()).filter(Boolean);
                                                                            setAdminModalSelected(existing);
                                                                            setAdminModalOpen(true);
                                                                        }}
                                                                        className="dropdown-item assign-admins"
                                                                    >
                                                                        👥 Assign Admins
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                    {activeTab === 'kiosks' && (
                        <div>
                            <AdminKiosks user={user} />
                        </div>
                    )}
                    {activeTab === 'meetingrooms' && (
                        <div>
                            <AdminMeetingRooms user={user} />
                        </div>
                    )}
                    {activeTab === 'database' && (
                        <div>
                            <AdminDatabase />
                        </div>
                    )}
                    {activeTab === 'messages' && (
                        <div>
                            <AdminMessages />
                        </div>
                    )}
                    {activeTab === 'flexpool' && (
                        <div>
                            <div className="section-header">
                                <h2>Manage Flex Pool</h2>
                                <button onClick={() => {
                                    setFormData({});
                                    setEditingId(null);
                                    setShowModal(true);
                                }} className="btn-add">
                                    + Add Desk to Flex Pool
                                </button>
                            </div>
                            {/* Flex Pool Filter Menu */}
                            <div className="filter-menu" style={{
                                backgroundColor: '#f9f9f9',
                                padding: '15px',
                                borderRadius: '5px',
                                marginBottom: '15px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '12px',
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>🔍 Desk ID</label>
                                        <input
                                            type="text"
                                            placeholder="Search desk ID..."
                                            value={flexPoolFilters.search}
                                            onChange={(e) => setFlexPoolFilters({ ...flexPoolFilters, search: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Zone</label>
                                        <select
                                            value={flexPoolFilters.zone}
                                            onChange={(e) => setFlexPoolFilters({ ...flexPoolFilters, zone: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <option value="">All Zones</option>
                                            {[...new Set(flexPoolDesks.map(d => d.zone).filter(Boolean))].sort().map(zone => (
                                                <option key={zone} value={zone}>{zone}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button
                                        onClick={() => setFlexPoolFilters({ search: '', zone: '' })}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: '#f0f0f0',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        ✕ Clear Filters
                                    </button>
                                </div>
                                <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#666' }}>
                                    Showing {getFilteredFlexPoolDesks().length} of {flexPoolDesks.length} flex pool desks
                                </p>
                            </div>
                            {loading ? (
                                <p>Loading...</p>
                            ) : (
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Desk ID</th>
                                            <th>Zone</th>
                                            <th>Unit</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getFilteredFlexPoolDesks().map(desk => (
                                            <tr key={desk.desk_id}>
                                                <td><strong>{desk.desk_id}</strong></td>
                                                <td>{desk.zone}</td>
                                                <td>{desk.unit || '-'}</td>
                                                <td>
                                                    <select
                                                        value={desk.status}
                                                        onChange={(e) => updateFlexPoolDeskStatus(desk.desk_id, e.target.value)}
                                                        style={{
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ccc',
                                                            backgroundColor: desk.status === 'active' ? '#d4edda' : '#f8d7da',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <option value="active">Active</option>
                                                        <option value="inactive">Inactive</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <button
                                                        onClick={() => removeDeskFromFlexPool(desk.desk_id)}
                                                        className="btn-delete"
                                                        style={{ padding: '6px 12px' }}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {showModal && (
                <div className="modal active">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>
                                {activeTab === 'users' && (editingId ? 'Edit User' : 'Add User')}
                                {activeTab === 'desks' && (editingId ? 'Edit Desk' : 'Add Desk')}
                                {activeTab === 'holidays' && 'Add Holiday'}
                                {activeTab === 'units' && formData.action === 'assign' && 'Assign Desks to Unit'}
                                {activeTab === 'units' && formData.action !== 'assign' && (editingId ? 'Edit Unit' : 'Add Unit')}
                                {activeTab === 'flexpool' && 'Add Desk to Flex Pool'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setZoneDesks([]);
                                    setSelectedDeskIds([]);
                                }}
                                className="close-btn"
                            >
                                ×
                            </button>
                        </div>

                        {activeTab === 'users' && (
                            <div className="form">
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input
                                        type="email"
                                        value={formData.email || ''}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Name</label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Unit</label>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <select
                                            value={formData.unit || ''}
                                            onChange={(e) => {
                                                if (e.target.value === '__ADD_NEW__') {
                                                    const newUnit = prompt('Enter new unit name:');
                                                    if (newUnit && newUnit.trim()) {
                                                        setFormData({ ...formData, unit: newUnit.trim() });
                                                        if (!availableUnits.includes(newUnit.trim())) {
                                                            setAvailableUnits([...availableUnits, newUnit.trim()].sort());
                                                        }
                                                    }
                                                } else {
                                                    setFormData({ ...formData, unit: e.target.value });
                                                }
                                            }}
                                            style={{ flex: 1 }}
                                        >
                                            <option value="">Select unit...</option>
                                            {availableUnits.map(unit => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                            <option value="__ADD_NEW__" style={{ fontWeight: 'bold', color: '#0072CE' }}>
                                                + Add New Unit...
                                            </option>
                                        </select>
                                        {formData.unit && !availableUnits.includes(formData.unit) && (
                                            <span style={{ color: '#0072CE', fontSize: '12px' }}>New</span>
                                        )}
                                    </div>
                                    <small style={{ color: '#666', fontSize: '12px' }}>
                                        Select from existing units or add a new one
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.is_admin || false}
                                            onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                                        />
                                        Is Admin
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Alternate Email</label>
                                    <input
                                        type="email"
                                        value={formData.alternate_email || ''}
                                        onChange={(e) => setFormData({ ...formData, alternate_email: e.target.value })}
                                        placeholder="Optional"
                                    />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
                                    <div className="form-group">
                                        <label>Country Code</label>
                                        <input
                                            type="text"
                                            value={formData.phone_country_code || ''}
                                            onChange={(e) => setFormData({ ...formData, phone_country_code: e.target.value })}
                                            placeholder="+62"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Phone Number</label>
                                        <input
                                            type="tel"
                                            value={formData.phone_number || ''}
                                            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                            placeholder="8126990502"
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Department</label>
                                    <input
                                        type="text"
                                        value={formData.department || ''}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Zone</label>
                                    <input
                                        type="text"
                                        value={formData.zone || ''}
                                        onChange={(e) => setFormData({ ...formData, zone: e.target.value })}
                                    />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="form-group">
                                        <label>Monitor Preference</label>
                                        <select
                                            value={formData.monitor_preference || ''}
                                            onChange={(e) => setFormData({ ...formData, monitor_preference: e.target.value })}
                                        >
                                            <option value="">Any</option>
                                            <option value="Single Monitor">Single Monitor</option>
                                            <option value="Dual Monitor">Dual Monitor</option>
                                            <option value="Adjustable Monitor">Adjustable Monitor</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Notification Pref.</label>
                                        <select
                                            value={formData.notification_preference || 'email'}
                                            onChange={(e) => setFormData({ ...formData, notification_preference: e.target.value })}
                                        >
                                            <option value="email">Email</option>
                                            <option value="whatsapp">WhatsApp</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Profile Picture Path</label>
                                    <input
                                        type="text"
                                        value={formData.profile_picture || ''}
                                        onChange={(e) => setFormData({ ...formData, profile_picture: e.target.value })}
                                        placeholder="/uploads/..."
                                    />
                                </div>

                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold', color: '#555' }}>🏢 Additional Units</label>
                                    <div style={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                        gap: '12px',
                                        padding: '12px',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd'
                                    }}>
                                        {availableUnits.map(unit => {
                                            const isMainUnit = unit === formData.unit;
                                            const additionalUnitsList = formData.additional_units 
                                                ? formData.additional_units.split(',').map(u => u.trim())
                                                : [];
                                            const isSelected = additionalUnitsList.includes(unit);
                                            
                                            return (
                                                <label key={unit} style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    cursor: isMainUnit ? 'not-allowed' : 'pointer',
                                                    opacity: isMainUnit ? 0.5 : 1
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected && !isMainUnit}
                                                        onChange={(e) => {
                                                            let updated = additionalUnitsList.filter(u => u !== unit);
                                                            if (e.target.checked && !isMainUnit) {
                                                                updated.push(unit);
                                                            }
                                                            setFormData({ 
                                                                ...formData, 
                                                                additional_units: updated.length > 0 ? updated.join(',') : '' 
                                                            });
                                                        }}
                                                        disabled={isMainUnit}
                                                        style={{ marginRight: '8px', cursor: isMainUnit ? 'not-allowed' : 'pointer' }}
                                                    />
                                                    <span style={{ fontSize: '13px' }}>
                                                        {unit} {isMainUnit ? '(Primary)' : ''}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    <small style={{ color: '#888', display: 'block', marginTop: '8px' }}>
                                        Check units to add them as additional access areas for this user
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold', color: '#555' }}>🔐 Locker Number</label>
                                    <input
                                        type="number"
                                        value={formData.locker_number || ''}
                                        onChange={(e) => setFormData({ ...formData, locker_number: e.target.value })}
                                        placeholder="Locker No."
                                    />
                                </div>

                                <div className="form-group">
                                    <label style={{ fontWeight: 'bold', color: '#0072CE' }}>🔐 Locker PIN</label>
                                    <input
                                        type="text"
                                        value={formData.locker_pin || '----'}
                                        readOnly
                                        style={{
                                            fontFamily: 'monospace',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            letterSpacing: '4px',
                                            backgroundColor: '#f0f8ff',
                                            color: '#0072CE',
                                            border: '2px solid #0072CE'
                                        }}
                                    />
                                    <small style={{ color: '#666' }}>4-digit PIN for locker access (auto-generated)</small>
                                </div>

                                <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '10px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!formData.is_admin}
                                                onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                                            />
                                            Is Admin
                                        </label>

                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!formData.unit_admin}
                                                onChange={(e) => setFormData({ ...formData, unit_admin: e.target.checked })}
                                            />
                                            Unit Admin
                                        </label>

                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={formData.show_avatar !== false} // Default true
                                                onChange={(e) => setFormData({ ...formData, show_avatar: e.target.checked })}
                                            />
                                            Show Avatar
                                        </label>

                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!formData.profile_complete}
                                                onChange={(e) => setFormData({ ...formData, profile_complete: e.target.checked })}
                                            />
                                            Profile Complete
                                        </label>

                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', backgroundColor: '#fff3cd', padding: '8px', borderRadius: '4px' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!formData.adjustable_monitor_priority}
                                                onChange={(e) => setFormData({ ...formData, adjustable_monitor_priority: e.target.checked })}
                                            />
                                            ♿ Adjustable Monitor Priority
                                        </label>
                                    </div>
                                    <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                                        Uncheck "Profile Complete" to force the user to go through the onboarding flow again.
                                    </small>
                                    <small style={{ color: '#856404', marginTop: '5px', display: 'block', backgroundColor: '#fff3cd', padding: '8px', borderRadius: '4px' }}>
                                        <strong>♿ Adjustable Monitor Priority:</strong> Users with this flag will automatically reassign any existing bookings when they book a desk with an adjustable monitor. The displaced user will be notified.
                                    </small>
                                </div>

                                <button onClick={handleSaveUser} className="btn-save" style={{ marginTop: '20px' }}>
                                    {editingId ? 'Update User' : 'Add User'}
                                </button>
                            </div>
                        )}

                        {activeTab === 'desks' && (
                            <div className="form">
                                <div className="form-group">
                                    <label>Desk ID *</label>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <input
                                            type="text"
                                            value={formData.id || ''}
                                            onChange={(e) => setFormData({ ...formData, id: e.target.value.trim() })}
                                            disabled={!!editingId}
                                            required
                                            placeholder="e.g., E1-1, W1-2"
                                            style={{ flex: 1 }}
                                        />
                                        {!editingId && formData.id && desks.find(d => d.id === formData.id) && (
                                            <span style={{ color: '#d32f2f', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                ⚠️ Already exists
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Zone *</label>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <select
                                            value={formData.zone || ''}
                                            onChange={(e) => {
                                                if (e.target.value === '__ADD_NEW__') {
                                                    const newZone = prompt('Enter new zone name:');
                                                    if (newZone && newZone.trim()) {
                                                        setFormData({ ...formData, zone: newZone.trim() });
                                                        if (!availableZones.includes(newZone.trim())) {
                                                            setAvailableZones([...availableZones, newZone.trim()].sort());
                                                        }
                                                    }
                                                } else {
                                                    setFormData({ ...formData, zone: e.target.value });
                                                }
                                            }}
                                            required
                                            style={{ flex: 1 }}
                                        >
                                            <option value="">Select zone...</option>
                                            {availableZones.map(zone => (
                                                <option key={zone} value={zone}>{zone}</option>
                                            ))}
                                            <option value="__ADD_NEW__" style={{ fontWeight: 'bold', color: '#0072CE' }}>
                                                + Add New Zone...
                                            </option>
                                        </select>
                                        {formData.zone && !availableZones.includes(formData.zone) && (
                                            <span style={{ color: '#0072CE', fontSize: '12px' }}>New</span>
                                        )}
                                    </div>
                                    <small style={{ color: '#666', fontSize: '12px' }}>
                                        Select from existing zones or add a new one
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label>Unit *</label>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <select
                                            value={formData.unit || ''}
                                            onChange={(e) => {
                                                if (e.target.value === '__ADD_NEW__') {
                                                    const newUnit = prompt('Enter new unit name:');
                                                    if (newUnit && newUnit.trim()) {
                                                        setFormData({ ...formData, unit: newUnit.trim() });
                                                        if (!availableUnits.includes(newUnit.trim())) {
                                                            setAvailableUnits([...availableUnits, newUnit.trim()].sort());
                                                        }
                                                    }
                                                } else {
                                                    setFormData({ ...formData, unit: e.target.value });
                                                }
                                            }}
                                            style={{ flex: 1 }}
                                            required
                                        >
                                            <option value="">Select unit...</option>
                                            <option value="All Unit" style={{ fontWeight: 'bold', color: '#28a745' }}>
                                                🌐 All Unit (Available to everyone)
                                            </option>
                                            {availableUnits.map(unit => (
                                                <option key={unit} value={unit}>{unit}</option>
                                            ))}
                                            <option value="__ADD_NEW__" style={{ fontWeight: 'bold', color: '#0072CE' }}>
                                                + Add New Unit...
                                            </option>
                                        </select>
                                        {formData.unit && formData.unit !== 'All Unit' && !availableUnits.includes(formData.unit) && (
                                            <span style={{ color: '#0072CE', fontSize: '12px' }}>New</span>
                                        )}
                                    </div>
                                    <small style={{ color: '#666', fontSize: '12px' }}>
                                        Select "All Unit" to make this desk available to everyone, or select a specific unit
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label>Monitor Setup</label>
                                    <select
                                        value={formData.monitor_setup || 'Single Monitor'}
                                        onChange={(e) => setFormData({ ...formData, monitor_setup: e.target.value })}
                                    >
                                        <option value="Single Monitor">Single Monitor</option>
                                        <option value="Dual Monitor">Dual Monitor</option>
                                        <option value="Adjustable Monitor">Adjustable Monitor</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Equipment</label>
                                    <input
                                        type="text"
                                        value={formData.equipment || ''}
                                        onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                                        placeholder="e.g., Headset, Dock"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.status || 'available'}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="available">Available</option>
                                        <option value="maintenance">Maintenance</option>
                                        <option value="reserved">Reserved</option>
                                    </select>
                                </div>
                                {formData.status === 'reserved' && (
                                    <div className="form-group">
                                        <label>Reserved For (User Email) *</label>
                                        <select
                                            value={formData.reserved_for || ''}
                                            onChange={(e) => setFormData({ ...formData, reserved_for: e.target.value })}
                                            required
                                        >
                                            <option value="">Select a user...</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.email}>
                                                    {u.name || u.email} ({u.unit || 'No unit'})
                                                </option>
                                            ))}
                                        </select>
                                        <small style={{ color: '#666', fontSize: '12px' }}>
                                            Only the selected user will be able to book this desk
                                        </small>
                                    </div>
                                )}

                                <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '15px' }}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>Desk Features</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!formData.has_display}
                                                onChange={(e) => setFormData({ ...formData, has_display: e.target.checked ? 1 : 0 })}
                                            />
                                            Has Display (ESP32)
                                        </label>

                                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!formData.adjustable_monitor}
                                                onChange={(e) => setFormData({ ...formData, adjustable_monitor: e.target.checked ? 1 : 0 })}
                                            />
                                            Adjustable Monitor
                                        </label>
                                    </div>
                                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                                        Check these boxes if the desk has these features
                                    </small>
                                </div>

                                <button onClick={handleSaveDesk} className="btn-save" style={{ marginTop: '15px' }}>
                                    {editingId ? 'Update Desk' : 'Add Desk'}
                                </button>
                            </div>
                        )}

                        {activeTab === 'holidays' && (
                            <div className="form">
                                <div className="form-group">
                                    <label>Date *</label>
                                    <input
                                        type="date"
                                        value={formData.date || ''}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Holiday Name</label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </div>
                                <button onClick={handleSaveHoliday} className="btn-save">
                                    Add Holiday
                                </button>
                            </div>
                        )}

                        {activeTab === 'flexpool' && (
                            <div className="form">
                                <div className="form-group">
                                    <label>Desk ID *</label>
                                    <select
                                        value={formData.desk_id || ''}
                                        onChange={(e) => {
                                            const selectedDesk = desks.find(d => d.id === e.target.value);
                                            setFormData({
                                                ...formData,
                                                desk_id: e.target.value,
                                                zone: selectedDesk?.zone || ''
                                            });
                                        }}
                                        required
                                    >
                                        <option value="">Select desk...</option>
                                        {desks.filter(d => !flexPoolDesks.find(fp => fp.desk_id === d.id)).map(desk => (
                                            <option key={desk.id} value={desk.id}>{desk.id} ({desk.zone})</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Zone *</label>
                                    <input
                                        type="text"
                                        value={formData.zone || ''}
                                        readOnly
                                        style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        if (!formData.desk_id) {
                                            setError('Please select a desk');
                                            return;
                                        }
                                        addDeskToFlexPool(formData.desk_id, formData.zone);
                                    }}
                                    className="btn-save"
                                >
                                    Add to Flex Pool
                                </button>
                            </div>
                        )}

                        {activeTab === 'units' && formData.action === 'assign' && (
                            <div className="form">
                                <div className="form-group">
                                    <label>Select Unit *</label>
                                    <select
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    >
                                        <option value="">Select a unit...</option>
                                        {availableUnits.map(unit => (
                                            <option key={unit} value={unit}>{unit}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Assign by Zone</label>
                                    <select
                                        value={formData.zone || ''}
                                        onChange={(e) => {
                                            const zone = e.target.value;
                                            setFormData({ ...formData, zone });
                                            fetchDesksByZone(zone);
                                        }}
                                    >
                                        <option value="">Select a zone...</option>
                                        {availableZones.map(zone => (
                                            <option key={zone} value={zone}>{zone}</option>
                                        ))}
                                    </select>
                                    <small style={{ color: '#666', fontSize: '12px' }}>
                                        Select a zone to view available desks
                                    </small>
                                </div>

                                {formData.zone && zoneDesks.length > 0 && (
                                    <div className="form-group">
                                        <label>
                                            Select Specific Desks (Optional)
                                            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                                                {selectedDeskIds.length > 0
                                                    ? `${selectedDeskIds.length} selected`
                                                    : 'Leave empty to assign all desks in zone'}
                                            </span>
                                        </label>
                                        <div style={{
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            padding: '10px',
                                            backgroundColor: '#f9f9f9'
                                        }}>
                                            <div style={{ marginBottom: '10px' }}>
                                                <label style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedDeskIds.length === zoneDesks.length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedDeskIds(zoneDesks.map(d => d.id));
                                                            } else {
                                                                setSelectedDeskIds([]);
                                                            }
                                                        }}
                                                        style={{ marginRight: '8px' }}
                                                    />
                                                    <strong>Select All ({zoneDesks.length} desks)</strong>
                                                </label>
                                            </div>
                                            <hr style={{ margin: '10px 0', borderColor: '#ddd' }} />
                                            {zoneDesks.map(desk => (
                                                <div key={desk.id} style={{ marginBottom: '8px' }}>
                                                    <label style={{ cursor: 'pointer', display: 'block', fontWeight: 'normal' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDeskIds.includes(desk.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedDeskIds([...selectedDeskIds, desk.id]);
                                                                } else {
                                                                    setSelectedDeskIds(selectedDeskIds.filter(id => id !== desk.id));
                                                                }
                                                            }}
                                                            style={{ marginRight: '8px' }}
                                                        />
                                                        <span style={{ fontWeight: 'bold' }}>Desk {desk.id}</span>
                                                        <span style={{ marginLeft: '10px', color: '#666', fontSize: '12px' }}>
                                                            {desk.unit ? `Currently: ${desk.unit}` : 'Unassigned'}
                                                            {desk.monitor_setup && ` • ${desk.monitor_setup}`}
                                                        </span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                        <small style={{ color: '#0072CE', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                                            💡 Tip: Select specific desks to assign only those, or leave all unchecked to assign the entire zone
                                        </small>
                                    </div>
                                )}

                                <button onClick={handleAssignDesksToUnit} className="btn-save">
                                    {selectedDeskIds.length > 0
                                        ? `Assign ${selectedDeskIds.length} Desk(s)`
                                        : formData.zone
                                            ? `Assign All Desks in ${formData.zone}`
                                            : 'Assign Desks'}
                                </button>
                            </div>
                        )}

                        {activeTab === 'units' && formData.action !== 'assign' && (
                            <div className="form">
                                <div className="form-group">
                                    <label>Unit Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name || ''}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Operations, Education, Health"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Unit Admins (Emails)</label>
                                    <textarea
                                        value={formData.unit_admins || ''}
                                        onChange={(e) => setFormData({ ...formData, unit_admins: e.target.value })}
                                        placeholder="user1@example.com, user2@example.com"
                                        rows={3}
                                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                    />
                                    <small style={{ color: '#666', fontSize: '12px' }}>Comma separated email addresses of users who can manage this unit</small>
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={formData.allow_reserve !== false}
                                            onChange={(e) => setFormData({ ...formData, allow_reserve: e.target.checked })}
                                        />
                                        Allow Reserved Desks
                                    </label>
                                    <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                                        When enabled, admins can reserve specific desks for users in this unit
                                    </small>
                                </div>
                                <button onClick={handleSaveUnit} className="btn-save">
                                    {editingId ? 'Update Unit' : 'Add Unit'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Status Change Modal */}
            {statusChangeModal && (
                <div className="modal-overlay">
                    <div className="modal-content status-change-modal">
                        <div className="modal-header">
                            <h2>Desk Status Change Impact</h2>
                            <button
                                onClick={() => {
                                    setStatusChangeModal(false);
                                    setImpactedBookings([]);
                                }}
                                className="close-btn"
                            >
                                ×
                            </button>
                        </div>

                        <div className="status-change-form">
                            <p><strong>Desk ID:</strong> {statusChangeData.id}</p>
                            <p><strong>New Status:</strong> {statusChangeData.status}</p>

                            <div className="form-group">
                                <label>Effect Period - Start Date *</label>
                                <input
                                    type="date"
                                    value={statusChangeData.startDate || ''}
                                    onChange={(e) => setStatusChangeData({ ...statusChangeData, startDate: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Effect Period - End Date</label>
                                <input
                                    type="date"
                                    value={statusChangeData.endDate || ''}
                                    onChange={(e) => setStatusChangeData({ ...statusChangeData, endDate: e.target.value })}
                                    placeholder="Leave empty for indefinite"
                                />
                            </div>

                            {statusChangeData.status === 'reserved' && (
                                <div className="form-group">
                                    <label>Allowed Users (comma-separated emails)</label>
                                    <textarea
                                        value={statusChangeData.reservedUsers || ''}
                                        onChange={(e) => setStatusChangeData({ ...statusChangeData, reservedUsers: e.target.value })}
                                        placeholder="user1@example.com, user2@example.com"
                                        rows="3"
                                    />
                                </div>
                            )}

                            {impactedBookings.length > 0 && (
                                <div className="impacted-bookings">
                                    <h3>⚠️ Impacted Bookings ({impactedBookings.length})</h3>
                                    <p>The following bookings will be cancelled and users will be notified:</p>
                                    <table className="impact-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>User</th>
                                                <th>Email</th>
                                                <th>Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {impactedBookings.map(booking => (
                                                <tr key={booking.id}>
                                                    <td>{new Date(booking.booking_date).toLocaleDateString()}</td>
                                                    <td>{booking.user_name || 'N/A'}</td>
                                                    <td>{booking.user_email}</td>
                                                    <td>
                                                        {booking.start_time && booking.end_time
                                                            ? `${booking.start_time} - ${booking.end_time}`
                                                            : 'Full Day'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button
                                    onClick={() => {
                                        setStatusChangeModal(false);
                                        setImpactedBookings([]);
                                    }}
                                    className="btn-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmStatusChange}
                                    className="btn-confirm-status"
                                >
                                    {impactedBookings.length > 0
                                        ? `Confirm & Cancel ${impactedBookings.length} Booking(s)`
                                        : 'Confirm Status Change'
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Device Reassignment Modal */}
            {deviceReassignModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '500px' }}>
                        <div className="modal-header">
                            <h2>Reassign Device to Another Desk</h2>
                            <button
                                onClick={() => {
                                    setDeviceReassignModal(false);
                                    setSelectedDeviceForReassign(null);
                                    setSelectedNewDeskForDevice('');
                                }}
                                className="close-btn"
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '20px' }}>
                            <div className="form-group">
                                <label><strong>Device ID:</strong></label>
                                <input
                                    type="text"
                                    value={selectedDeviceForReassign || ''}
                                    disabled
                                    style={{ backgroundColor: '#f5f5f5' }}
                                />
                            </div>

                            <div className="form-group">
                                <label><strong>Select New Desk *</strong></label>
                                <select
                                    value={selectedNewDeskForDevice}
                                    onChange={(e) => setSelectedNewDeskForDevice(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        boxSizing: 'border-box'
                                    }}
                                >
                                    <option value="">-- Choose a desk --</option>
                                    {desks.map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.id} - {d.zone} - {d.unit}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{
                                backgroundColor: '#f0f8ff',
                                padding: '12px',
                                borderRadius: '4px',
                                marginBottom: '16px',
                                fontSize: '13px',
                                color: '#333'
                            }}>
                                <strong>ℹ️ Note:</strong> This will reassign the device to the selected desk. The device will start monitoring the new desk's booking status.
                            </div>

                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => {
                                        setDeviceReassignModal(false);
                                        setSelectedDeviceForReassign(null);
                                        setSelectedNewDeskForDevice('');
                                    }}
                                    className="btn-cancel"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeviceReassign}
                                    className="btn-confirm-status"
                                    disabled={!selectedNewDeskForDevice}
                                    style={{
                                        opacity: selectedNewDeskForDevice ? 1 : 0.5,
                                        cursor: selectedNewDeskForDevice ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    Reassign Device
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Unit Admins Modal */}
            {adminModalOpen && (
                <div className="modal active">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>Assign Unit Admins — {adminModalUnit}</h2>
                            <button
                                onClick={() => setAdminModalOpen(false)}
                                className="close-btn"
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <p style={{ marginBottom: '8px' }}>Select users to assign as unit admins for <strong>{adminModalUnit}</strong>:</p>
                            <div style={{ maxHeight: '240px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '6px' }}>
                                {/* Only show users who belong to this unit */}
                                {users.filter(u => u.unit === adminModalUnit).map(u => (
                                    <label key={u.id} style={{ display: 'block', marginBottom: '6px' }}>
                                        <input
                                            type="checkbox"
                                            checked={adminModalSelected.includes(u.email)}
                                            onChange={(e) => {
                                                if (e.target.checked) setAdminModalSelected([...adminModalSelected, u.email]);
                                                else setAdminModalSelected(adminModalSelected.filter(x => x !== u.email));
                                            }}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {u.name || u.email} ({u.email})
                                    </label>
                                ))}
                                {users.filter(u => u.unit === adminModalUnit).length === 0 && (
                                    <div style={{ color: '#666' }}>No users in this unit. Add users to <strong>{adminModalUnit}</strong> first.</div>
                                )}
                            </div>

                            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={async () => {
                                        try {
                                            await axios.put(`/api/admin/units/${encodeURIComponent(adminModalUnit)}/admins`, { unit_admins: adminModalSelected });
                                            setSuccess('Unit admins updated');
                                            setAdminModalOpen(false);
                                            fetchUnits();
                                        } catch (err) {
                                            setError(err.response?.data?.error || 'Failed to update unit admins');
                                        }
                                    }}
                                    className="btn-save"
                                >
                                    Save Admins
                                </button>
                                <button onClick={() => setAdminModalOpen(false)} className="btn-cancel">Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPage;
